/* eslint-disable no-param-reassign */
import * as React from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';
import { getLink, getLinkPath } from '@models/Batch';
import { getCommaSeparatedUIDs } from '@utils/textHelper';
import { Spacings, mediaQuery } from '@styles/index';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import { SEARCH_RESULT_CLICKED, SEARCH_RESULT_PAGE } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import AquillaBatchCard from 'aquilla/core/organisms/BatchCard';
import { getUserById } from '@selectors/learner';
import pushRoute from '@utils/pushRoute';
import { EDUCATOR_TYPES } from '@cont/Batch/utils';
import { authorsInfo, getDateInfo, getBatchTag } from './util';

const CourseThumbnail = styled(Thumbnail)`
  border-top-left-radius: ${Spacings.SPACING_2B};
  border-top-right-radius: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoTablet} {
    width: 100%;
    height: 190px;
  }
`;

interface IBatchCard {
  orientation?: 'horizontal' | 'vertical';
  firstAuthorUsername?: string;
  batch: any;
  index: number;
  dataAnalytics?: string;
  type?: string;
  searchData?: any;
  hideEnroll?: boolean;
  isFeedCard?: boolean;
  isEnrolled?: boolean;
  onClick?: () => void;
  updateRecentSearchLog?: () => void;
  isPartnerEducator?: boolean;
}

const BatchCard: React.FC<IBatchCard> = ({
  orientation = 'vertical',
  firstAuthorUsername = '',
  dataAnalytics = '',
  type = '',
  searchData = {},
  onClick = () => {},
  updateRecentSearchLog = () => {},
  batch = {},
  index = 0
}) => {
  const dispatch = useDispatch();
  const batchGoalUID = batch?.goal?.uid || '';
  const { startsAt, endsAt, timeType } = batch || {};
  const firstUser = useSelector(getUserById(firstAuthorUsername));
  const meUID = useMyInfo(getMeUid);
  const { languages, name } = batch;
  const authors = batch.authors?.map((author) => ({
    ...author,
    name: author.name || `${author.firstName} ${author.lastName}`
  }));
  // @ts-ignore
  const goalSegmentData = useGoalEventProps({ batchGoalUID });
  if (!batch) return null;

  const isPartnerEducator = batch.educatorType === EDUCATOR_TYPES.PARTNER;

  const handleAnalytics = () => {
    if (onClick) onClick();
    const batchUID = batch.uid || '';
    if (type === 'search') {
      const nSearchData = { ...searchData };
      delete nSearchData.query;
      const position = nSearchData.position ? nSearchData.position : 'See all';
      segment.track(
        SEARCH_RESULT_CLICKED,
        {
          ...goalSegmentData,
          ...nSearchData,
          sub_position: index + 1,
          educator_uid: getCommaSeparatedUIDs(authors),
          educator_username: getCommaSeparatedUIDs(authors, 'username'),
          educator_name: getCommaSeparatedUIDs(authors, 'name'),
          last_primary_source: SEARCH_RESULT_PAGE,
          last_primary_source_section: 'Batches',
          search_section: 'Global',
          current_screen: 'Batches',
          eventName: SEARCH_RESULT_CLICKED,
          eventType: 'click',
          version: 2,
          objectID: `${searchData.object_type}:${batchUID}`,
          userToken: meUID,
          ...nSearchData,
          position
        },
        evtNameCategoryMap.WITHOUT_CATEGORY
      );
      dispatch(
        // @ts-ignore
        updateRecentSearchLog({
          ...searchData,
          entity_id: `${searchData.object_type}:${batchUID}`,
          goal_uid: batchGoalUID
        })
      );
    }
    const route = {
      href: getLinkPath(isPartnerEducator),
      as: getLink(batch, isPartnerEducator)
    };
    pushRoute(route.href, route.as);
  };

  const renderLanguages = () => {
    const languagesToShow =
      languages?.length > 3 ? languages.slice(0, 3) : languages;
    const labelLanguages = languagesToShow?.map(({ label }) => label) || [];
    return labelLanguages.toString();
  };

  const thumbnail = () => (
    <CourseThumbnail
      imgUrl={batch.thumbnail || batch.coverPhoto}
      orientation={orientation}
      data-analytics={`${dataAnalytics}Card`}
    />
  );

  return (
    <AquillaBatchCard
      thumbnail={thumbnail}
      batchTiming={timeType?.text}
      educatorNames={authorsInfo(firstUser, authors)}
      href={getLink(batch, isPartnerEducator)}
      langLabel={renderLanguages()}
      onClick={handleAnalytics}
      startsIn={getDateInfo(startsAt, endsAt)}
      title={name}
      topicColor="var(--color-text-primary)"
      topicName={getBatchTag(batch)}
      variant="default"
    />
  );
};

export default BatchCard;
